const EXPLICIT_NON_EMAIL_TYPES = new Set(["qsUsername", "discordUsername"]);
const EMAIL_SHAPED = /^[^@\s]+@[^@\s]+$/;

export function isEmailShaped(value) {
  return EMAIL_SHAPED.test(String(value || "").trim());
}

export function isExplicitNonEmailType(type) {
  return EXPLICIT_NON_EMAIL_TYPES.has(type);
}

export function buildPublicIdentifier(user) {
  // Accept null/undefined safely: a default param only catches `undefined`, but
  // render call sites (getUserLabel(friend), getUserIdentity(owner), …) can pass
  // `null`, and a thrown destructure here crashes the whole React subtree.
  const {
    publicIdentifier,
    publicIdentifierType,
    qsUsername,
    discordUsername,
    displayName,
    email,
  } = user || {};
  const type = publicIdentifierType || "email";
  const isExplicitNonEmail = EXPLICIT_NON_EMAIL_TYPES.has(type);
  // Honor a precomputed identifier, but never let a stale email-shaped mirror
  // leak when the user explicitly chose a non-email identifier type.
  if (publicIdentifier && !(isExplicitNonEmail && isEmailShaped(publicIdentifier))) {
    return publicIdentifier;
  }
  // A non-email identifier type must never fall back to an email-shaped
  // displayName, or we'd leak the email under a type the user chose to hide it.
  const safeDisplayName = isEmailShaped(displayName) ? "" : displayName || "";
  if (type === "qsUsername") {
    return qsUsername ? `@${qsUsername}` : safeDisplayName;
  }
  if (type === "discordUsername") {
    return discordUsername || safeDisplayName;
  }
  return email || "";
}

export function getUserIdentity(user = {}) {
  const displayName = String(user?.displayName || "").trim();
  const publicIdentifier = buildPublicIdentifier(user);
  // Mirror buildPublicIdentifier's guard: when the user EXPLICITLY chose a
  // non-email identifier type, never let an email-shaped displayName leak into
  // the label. An absent/null type means DEFAULT EMAIL, so an email-shaped
  // displayName is allowed there (unchanged for email-type + untyped users).
  const isExplicitNonEmail = EXPLICIT_NON_EMAIL_TYPES.has(
    user?.publicIdentifierType
  );
  const safeDisplayName =
    isExplicitNonEmail && isEmailShaped(displayName) ? "" : displayName;
  const normalizedDisplay = safeDisplayName.toLowerCase();
  const normalizedPublic = String(publicIdentifier || "").toLowerCase();
  const isDuplicate = Boolean(
    safeDisplayName && publicIdentifier && normalizedDisplay === normalizedPublic
  );
  const label =
    safeDisplayName && !isDuplicate
      ? safeDisplayName
      : publicIdentifier || safeDisplayName;
  return { displayName, publicIdentifier, label, isDuplicate };
}

export function getUserLabel(user = {}) {
  return getUserIdentity(user).label || "";
}

export function getUserAvatarUrl(user = {}) {
  if (!user) return null;
  return (
    user.avatar ||
    user.photoURL ||
    user.photoUrl ||
    user.userAvatar ||
    user.avatarUrl ||
    user.customAvatarUrl ||
    null
  );
}
